<template>
  <div>
    <el-button @click="dialogVisible = true" type="primary">预览Excel</el-button>

    <el-dialog
      :visible.sync="dialogVisible"
      fullscreen
      append-to-body
      @opened="fetchData"
      destroy-on-close
      custom-class="excel-preview-dialog"
    >
      <template #title>
        <div class="box-title">
          <el-button type="primary" @click="download" :loading="downloadLoading">下载文件 - {{ localFilename }}</el-button>
          <el-alert type="info" :closable="false" title="此页面仅供查看，修改无效，修改请下载后操作"></el-alert>
        </div>
      </template>
      <div class="error" v-if="renderError">解析失败，请下载后查看</div>
      <div class="spreadsheet-container" v-loading="loading" ref="spreadsheetRef">
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import contentDisposition from 'content-disposition';
import _ from 'lodash';
import * as Excel from 'exceljs/dist/exceljs';
import Spreadsheet from 'x-data-spreadsheet';
import moment from 'moment';

@Component
export default class ExcelPreview extends Vue {
  @Prop({ type: String, default: 'data.xlsx' }) filename;
  @Prop(Function) onRequest;
  @Prop({ type: Object, default: () => ({}) }) config;

  dialogVisible = false;
  loading = false;
  downloadLoading = false;

  localFilename = '';
  renderError = false;

  getAttachmentNameFromHeader(header) {
    const cd = header['content-disposition'] || header['Content-Disposition'];
    if (cd) {
      try {
        const result = contentDisposition.parse(cd);
        return _.get(result, 'parameters.filename');
      } catch (e) {
        return this.filename;
      }
    }
    return this.filename;
  }

  async download() {
    this.downloadLoading = true;
    try {
      const result = await this.requestData('blob');
      const a = new Blob([result.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(a);
      const aLink = document.createElement('a');
      aLink.href = url;
      aLink.download = this.localFilename;
      aLink.click();
    } finally {
      this.downloadLoading = false;
    }
  }

  requestData(responseType = 'arraybuffer') {
    if (!this.onRequest) {
      throw new Error('onRequest is required');
    }
    return this.onRequest({ responseType, return_res: true });
  }

  renderExcel(xs, buffer) {
    try {
      const wb = new Excel.Workbook();
      // 微软的 Excel ColorIndex 一个索引数字对应一个颜色
      wb.xlsx.load(buffer).then(workbook => {
        let workbookData = []
        workbook.eachSheet((sheet) => {
          // 构造x-data-spreadsheet 的 sheet 数据源结构
          let sheetData = { name: sheet.name, styles: [], rows: {}, merges: [] }
          // 收集合并单元格信息
          let mergeAddressData = []
          for (let mergeRange in sheet._merges) {
            sheetData.merges.push(sheet._merges[mergeRange].shortRange)
            let mergeAddress = {}
            // 合并单元格起始地址
            mergeAddress.startAddress = sheet._merges[mergeRange].tl
            // 合并单元格终止地址
            mergeAddress.endAddress = sheet._merges[mergeRange].br
            // Y轴方向跨度
            mergeAddress.YRange = sheet._merges[mergeRange].model.bottom - sheet._merges[mergeRange].model.top
            // X轴方向跨度
            mergeAddress.XRange = sheet._merges[mergeRange].model.right - sheet._merges[mergeRange].model.left
            mergeAddressData.push(mergeAddress)
          }
          sheetData.cols = {}
          for (let i = 0;i < sheet.columns.length; i++)
          {
            sheetData.cols[i.toString()] = {}
            if (sheet.columns[i].width) {
              // 不知道为什么从 exceljs 读取的宽度显示到 x-data-spreadsheet 特别小, 这里乘以8
              sheetData.cols[i.toString()].width = sheet.columns[i].width * 8
            } else {
              // 默认列宽
              sheetData.cols[i.toString()].width = 100
            }
          }

          // 遍历行
          sheet.eachRow((row, rowIndex) => {
            sheetData.rows[(rowIndex - 1).toString()] = { cells: {} }
            //includeEmpty = false 不包含空白单元格
            row.eachCell({ includeEmpty: true }, function(cell, colNumber) {
              let cellText = ''
              if (cell.value && cell.value.result) {
                // Excel 单元格有公式
                cellText = cell.value.result
              } else if (cell.value && cell.value.richText) {
                // Excel 单元格是多行文本
                for (let text in cell.value.richText) {
                  // 多行文本做累加
                  cellText += cell.value.richText[text].text
                }
              } else if (cell.value instanceof Date) {
                // 处理日期类型
                try {
                  cellText = moment(cell.value).format('YYYY-MM-DD')
                } catch (e) {
                  cellText = cell.value
                }
              } else {
                // Excel 单元格无公式
                cellText = cell.value === 0 ? '0' : cell.value
              }

              // exceljs 对齐的格式转成 x-date-spreedsheet 能识别的对齐格式
              if (cell.style.alignment) {
                // 加默认对齐方式
                cell.style.alignment.vertical = cell.style.alignment.vertical || 'bottom'

                cell.style.align = cell.style.alignment.horizontal
                cell.style.valign = cell.style.alignment.vertical
              }

              //处理合并单元格
              let mergeAddress = _.find(mergeAddressData, function(o) { return o.startAddress === cell._address })
              if (mergeAddress) {
                // 遍历的单元格属于合并单元格
                if (cell.master.address !== mergeAddress.startAddress) {
                  // 不是合并单元格中的第一个单元格不需要计入数据源
                  return
                }
                // 说明是合并单元格区域的起始单元格
                sheetData.rows[(rowIndex - 1).toString()].cells[(colNumber - 1).toString()] = {
                  text: cellText,
                  style: 0,
                  merge: [mergeAddress.YRange, mergeAddress.XRange]
                }
                sheetData.styles.push(cell.style)
                //对应的style存放序号
                sheetData.rows[(rowIndex - 1).toString()].cells[(colNumber - 1).toString()].style = sheetData.styles.length - 1
              } else {
                // 非合并单元格
                sheetData.rows[(rowIndex - 1).toString()].cells[(colNumber - 1).toString()] = {
                  text: cellText,
                  style: 0
                }
                //解析单元格,包含样式
                sheetData.styles.push(cell.style)
                //对应的style存放序号
                sheetData.rows[(rowIndex - 1).toString()].cells[(colNumber - 1).toString()].style = sheetData.styles.length - 1
              }
            });
          })
          workbookData.push(sheetData)
        })
        xs.loadData(workbookData);
      })
    } catch (e) {
      xs.loadData({})
    }
  }

  async fetchData() {
    try {
      this.loading = true;
      this.renderError = false;
      const { data, headers } = await this.requestData();

      this.localFilename = this.getAttachmentNameFromHeader(headers);

      // 计算最大行数和列数
      const workbook = new Excel.Workbook();
      await workbook.xlsx.load(data);
      let maxColLen = 0;
      let maxRowLen = 0;
      workbook.eachSheet(sheet => {
        const sheetMaxCol = sheet.actualColumnCount || sheet.columnCount;
        if (sheetMaxCol > maxColLen) {
          maxColLen = sheetMaxCol;
        }

        // 遍历行以确保获取到实际的最大行数
        sheet.eachRow((row, rowIndex) => {
          if (rowIndex > maxRowLen) {
            maxRowLen = rowIndex;
          }
        });
      });

      this.xspreadsheet = new Spreadsheet(this.$refs.spreadsheetRef, _.merge({
        mode: 'edit',
        showToolbar: false,
        showBottomBar: true,
        showContextmenu: false,
        row: {
          height: 30,
          len: maxRowLen + 5,
        },
        col: {
          width: 150,
          len: maxColLen + 5,
        },
        style: {
          textwrap: false,
        },
        view: {
          width: () => {
            return this.$refs.spreadsheetRef.clientWidth;
          },
          height: () => {
            return this.$refs.spreadsheetRef.clientHeight;
          }
        }
      }, this.config));

      this.renderExcel(this.xspreadsheet, data);
    // eslint-disable-next-line keyword-spacing
    } catch(e) {
      console.error(e);
      this.renderError = true;
    } finally {
      this.loading = false;
    }
  }
}
</script>

<style lang="scss">
.excel-preview-dialog {
  display: flex;
  flex-direction: column;

  .el-dialog__body {
    flex: 1;
    overflow: hidden;
    padding: 0;

    .x-spreadsheet {
      height: 100%;
      display: flex;
      flex-direction: column;

      .x-spreadsheet-sheet {
        flex: 1;
      }
    }
  }

  .el-dialog__header,
  .el-dialog__footer {
    flex-shrink: 0;
  }
}
</style>

<style lang="scss" scoped>
.spreadsheet-container {
  width: 100%;
  height: 100%;
  overflow: auto;
}

.error {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  text-align: center;
  min-height: 100px;
  font-size: 15px;
  color: red;
  font-weight: bold;
}

.box-title {
  display: flex;
  align-items: center;
  gap: 10px;

  .el-alert {
    max-width: 400px;
  }
}
</style>
